import { Link, Navigate } from "react-router-dom";
import { Truck, Briefcase, ArrowUpRight, BarChart3, Boxes, DatabaseBackup, Siren, UserRound, Gauge, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/components/common";
import { PleinCta } from "@/components/Pleins";
import { fDate } from "@/lib/api";

function CongesHome() {
  const { data } = useFetch("/m2/conges");
  const p = data?.open;
  if (!p) return null;
  return (
    <Link to="/commerciaux/espace" className={`mb-6 max-w-2xl block rounded-xl px-5 py-3 text-sm font-semibold border ${p.overdue ? "bg-rose-50 border-rose-300 text-rose-800" : "bg-sky-50 border-sky-200 text-sky-900"}`} data-testid="home-conges-banner">
      {p.overdue ? "Relevé de retour de congés manquant — " : ""}Véhicule actuellement déclaré en période de congés depuis le {fDate(p.start_date)} · Terminer mes congés / relever le compteur
    </Link>
  );
}

function ReleveReminder() {
  const d = new Date();
  const kind = d.getDay() === 5 && d.getHours() >= 15 ? "vendredi" : d.getDay() === 1 && d.getHours() < 12 ? "lundi" : null;
  const { data } = useFetch(kind ? "/readings" : null);
  if (!kind || !data) return null;
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (data.some((r) => r.type === kind && r.date === iso)) {
    return <div className="mb-6 max-w-2xl flex items-center gap-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-5 py-3 text-sm font-semibold" data-testid="home-releve-done"><CheckCircle2 className="w-5 h-5" /> Relevé du {kind} {kind === "vendredi" ? "soir" : "matin"} enregistré, merci !</div>;
  }
  return (
    <Link to="/commerciaux/releve" className="mb-6 max-w-2xl flex items-center gap-3 rounded-xl bg-[#F49619] text-[#0E2D43] px-5 py-4 font-semibold hover:brightness-105 transition-[filter]" data-testid="home-releve-reminder">
      <Gauge className="w-6 h-6 shrink-0" />
      <span className="flex-1">Pensez à faire votre relevé kilométrique du {kind} {kind === "vendredi" ? "soir" : "matin"}.</span>
      <span className="inline-flex items-center gap-1 text-sm font-bold">Faire le relevé <ArrowUpRight className="w-4 h-4" /></span>
    </Link>
  );
}

function PendingBadge({ p, testid }) {
  if (!p?.total) return null;
  return (
    <div className="absolute top-7 left-7 h-11 flex items-center gap-2 pl-1.5 pr-3.5 rounded-full bg-[#0E2D43]/70 backdrop-blur-md border border-white/15" title={p.detail} data-testid={`${testid}-pending`}>
      <span className="min-w-8 h-8 px-2 rounded-full bg-[#F49619] text-[#0E2D43] grid place-items-center font-display font-extrabold text-sm" data-testid={`${testid}-pending-count`}>{p.total > 99 ? "99+" : p.total}</span>
      <span className="text-xs font-semibold text-white/90">{p.label || `action${p.total > 1 ? "s" : ""} en attente`}</span>
    </div>
  );
}

function ModuleCard({ to, icon: Icon, overline, title, text, points, img, testid, delay, pending }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group relative rise overflow-hidden rounded-2xl bg-[#0E2D43] text-white h-[480px] sm:h-[420px] flex flex-col p-7 transition-transform duration-300 hover:-translate-y-1 hover:shadow-2xl"
      style={{ animationDelay: `${delay}ms` }}
    >
      <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-40 group-hover:scale-105 transition-[opacity,transform] duration-500" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0E2D43] via-[#0E2D43]/80 to-transparent" />
      <div className="absolute top-7 right-7 w-11 h-11 rounded-full bg-[#F49619] text-[#0E2D43] grid place-items-center group-hover:rotate-45 transition-transform duration-300">
        <ArrowUpRight className="w-5 h-5" />
      </div>
      <PendingBadge p={pending} testid={testid} />
      <div className="relative flex flex-col h-full pt-[88px]">
        <div className="h-8 mb-5 flex items-center"><Icon className="w-8 h-8 text-[#F49619]" strokeWidth={1.75} data-testid={`${testid}-icon`} /></div>
        <div className="h-4 text-[11px] leading-4 uppercase tracking-[0.22em] font-bold text-white/60">{overline}</div>
        <h2 className="mt-2 h-[4.5rem] flex items-start font-display text-3xl leading-9 font-extrabold" data-testid={`${testid}-title`}>{title}</h2>
        <p className="mt-3 h-24 sm:h-[4.5rem] text-sm leading-6 text-white/75 overflow-hidden">{text}</p>
        <div className="mt-auto flex flex-wrap gap-2">
          {points.map((p) => (
            <span key={p} className="h-7 px-3 inline-flex items-center text-xs leading-none rounded-full bg-white/10 border border-white/15">{p}</span>
          ))}
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const { user } = useAuth();
  const { data: pend } = useFetch("/home/pending");
  if (user.role === "administratif") return <Navigate to="/documents" replace />;
  const admin = user.role === "admin";
  const showLiv = admin || user.role === "chauffeur";
  const showCom = admin || user.role === "commercial" || !!user.m2_self;
  return (
    <div data-testid="home-page">
      <div className="mb-10 rise">
        <div className="text-[11px] uppercase font-bold tracking-[0.2em] text-[#F49619] mb-2">Bonjour {user.name}</div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#0E2D43]">Choisissez votre espace</h1>
      </div>
      {pend?.backup && (
        <Link to="/parametres/sauvegarde" className="mb-6 flex items-center gap-3 rounded-xl bg-[#F49619] text-[#0E2D43] px-5 py-4 font-semibold hover:brightness-105 transition-[filter]" data-testid="home-backup-alert">
          <DatabaseBackup className="w-6 h-6 shrink-0" />
          <span className="flex-1">{pend.backup.last ? `Dernière sauvegarde il y a ${pend.backup.days} jours — pensez à télécharger une nouvelle sauvegarde complète.` : "Aucune sauvegarde n'a encore été téléchargée — pensez à télécharger la sauvegarde complète."}</span>
          <span className="hidden sm:inline-flex items-center gap-1 text-sm font-bold">Sauvegarder <ArrowUpRight className="w-4 h-4" /></span>
        </Link>
      )}
      {(user.role === "commercial" || user.m2_self) && <CongesHome />}
      {(user.role === "commercial" || user.m2_self) && <ReleveReminder />}
      {(user.role === "commercial" || user.m2_self) && (
        <div className="mb-6 max-w-2xl grid sm:grid-cols-2 gap-3">
          <PleinCta testid="home-enregistrer-plein" />
          <Link to="/commerciaux/espace?tab=sinistres" className="flex items-center justify-center gap-2 h-12 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold transition-colors" data-testid="home-declarer-sinistre"><Siren className="w-5 h-5" /> Déclarer un accident / sinistre</Link>
          <Link to="/commerciaux/espace" className="sm:col-span-2 flex items-center justify-center gap-2 h-12 rounded-xl bg-[#0E2D43] hover:bg-[#0A2233] text-white font-semibold transition-colors" data-testid="home-mon-espace"><UserRound className="w-5 h-5" /> Mon espace : véhicule, état des lieux, permis, sinistres</Link>
        </div>
      )}
      <div className={`grid gap-6 ${showLiv && showCom ? "md:grid-cols-2 xl:grid-cols-3" : showLiv ? "lg:grid-cols-2" : "max-w-2xl"}`}>
        {showLiv && (
          <ModuleCard
            to={admin ? "/livraison/dashboard" : "/livraison/controle"}
            icon={Truck}
            overline="Module 1"
            title="Véhicules de livraison"
            text="Contrôles quotidiens depuis le smartphone, suivi des anomalies et état de la flotte de livraison."
            points={["Contrôle véhicule", "Anomalies", "Fiches véhicules"]}
            img="/flotte-presthyg.jpg"
            testid="home-card-livraison"
            pending={pend?.livraison}
            delay={0}
          />
        )}
        {showCom && (
          <ModuleCard
            to={admin ? "/commerciaux/dashboard" : "/commerciaux/releve"}
            icon={Briefcase}
            overline="Module 2"
            title="Véhicules commerciaux"
            text="Relevés vendredi soir / lundi matin, pleins Fleet Pro, kilomètres personnels et dépenses carburant."
            points={["Relevé kilométrique", "Enregistrer un plein", "Carburant"]}
            img="https://images.unsplash.com/photo-1549399542-7e3f8b79c341?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
            testid="home-card-commerciaux"
            pending={pend?.commerciaux}
            delay={120}
          />
        )}
        {showLiv && (
          <ModuleCard
            to={admin ? "/materiel/dashboard" : "/materiel/controle"}
            icon={Boxes}
            overline="Module 3"
            title="Matériel entrepôt"
            text="Contrôles mensuels, entretien et suivi des équipements de manutention."
            points={["Contrôle mensuel", "Anomalies", "DEKRA"]}
            img="/materiel-entrepot.jpg"
            testid="home-card-materiel"
            pending={pend?.materiel}
            delay={240}
          />
        )}
      </div>
      {admin && (
        <Link to="/dashboard" data-testid="home-global-dashboard-link" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0E2D43] hover:text-[#E0830B] transition-colors">
          <BarChart3 className="w-4 h-4" /> Voir le tableau de bord général
        </Link>
      )}
    </div>
  );
}
